import { m } from 'framer-motion'
// @mui
import { styled } from '@mui/material/styles'
import { Link as RouterLink } from 'react-router-dom'
import { Button, Box, Container, Typography, Stack, Divider, Grid } from '@mui/material'

import { MotionViewport, varFade } from '../../components/animate'

import { PATH_PAGE } from '../../routes/paths'
import Iconify from '../../components/iconify'

import { AWS_CDN_DOMAIN } from 'src/config-global'

const StyledRoot = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(10, 0),
  [theme.breakpoints.up('md')]: {
    paddingBottom: '40px',
    paddingTop: '40px',
    height: 'auto',
  },
  paddingLeft: '20px',
  paddingTop: '40px',
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    bottom: 0,
    right: 0,
    height: '10px',
    backgroundColor: '#fff',
  },
}))

const StyledBackground = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0, // change this value as needed
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: -1,
  backgroundImage: `url(${AWS_CDN_DOMAIN + '/frontend-files/HomeSupplyChain.png'})`,
  backgroundSize: 'cover',
  backgroundPosition: 'top',
  filter: 'blur(0px)', // Adjust the blur intensity as needed
  opacity: 0.5, // Adjust the opacity as needed

  // Make image top fading in white gradient
  '&::before': {
    content: "''",
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '100%',
    background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.8) 100%)',
  },
}))

const svgImg = `<svg width="579" height="700" viewBox="0 0 579 700" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M16.729 1H578V699H16.729V594.321C16.729 588.719 18.9323 583.342 22.8631 579.351C33.5723 568.477 31.1319 550.419 17.9204 542.777L11.6117 539.129C-0.991193 531.839 -2.5543 514.258 8.56495 504.86C13.7424 500.484 16.729 494.049 16.729 487.27V1Z" stroke="#FFA400" stroke-width="2"/>
</svg>
`

// ----------------------------------------------------------------------

export default function HomeSupplyChain() {
  return (
    <StyledRoot
      sx={{
        backgroundImage: `url(${svgImg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'left bottom',
        opacity: 0.8,
      }}
    >
      <StyledBackground />
      <Container component={MotionViewport}>
        <Box
          gap={{ xs: 3, lg: 5 }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'left',
            justifyContent: 'left',
            textAlign: 'left',
            color: 'common.white',
            maxWidth: 1400,
            mt: 5,
          }}
        >
          <m.div>
            <Typography
              variant="h5"
              fontSize={{ xs: 30, md: 50 }}
              fontWeight={600}
              sx={{
                textAlign: 'left',
                lineHeight: 1,
                color: 'common.black',
                fontFamily:
                  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif', // Use Apple system font
              }}
            >
              Get Industry-Leading Supply Chain Solutions Under One Logistics Partner
            </Typography>
          </m.div>

          <Stack sx={{ alignItems: 'left' }}>
            <Grid container spacing={3} sx={{ mt: 3, maxWidth: 1000 }}>
              <Stack direction="row" spacing={2} sx={{ alignItems: 'left' }}>
                <Iconify
                  icon="ph:truck-light"
                  width={160}
                  sx={{ color: '#000', transition: 'color 0.3s ease-in-out' }}
                  onMouseEnter={(e) => (e.target.style.color = '#000')}
                  onMouseLeave={(e) => (e.target.style.color = '#000')}
                />

                <div>
                  <m.div variants={varFade().in}>
                    <Typography
                      variant="h5"
                      sx={{
                        color: 'common.black',
                      }}
                    >
                      Domestic
                    </Typography>
                  </m.div>
                  <m.div variants={varFade().inUp}>
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: 'left',
                        lineHeight: 1.3,
                        mb: 3,
                        fontSize: '1.2rem',
                        color: 'common.black',
                      }}
                    >
                      Our deep expertise in transportation, vast network of carriers, and innovative technology drive our commitment to
                      delivering on the promises we make to our clients.
                    </Typography>
                  </m.div>
                  <Button
                    component={RouterLink}
                    to={PATH_PAGE.services}
                    size="small"
                    variant="outlined"
                    endIcon={<Iconify icon="ic:round-arrow-right-alt" width={24} />}
                    sx={{
                      color: 'common.black',
                      transition: 'background-color 0.3s ease-in-out, color 0.3s ease-in-out',
                      '&:hover': {
                        color: 'primary.main',
                        transform: 'translateY(-5px)',
                        transition: 'transform 0.3s ease-in-out',
                      },
                    }}
                  >
                    Learn More
                  </Button>
                </div>
              </Stack>
            </Grid>
            <Divider sx={{ m: 1 }} />
            <Grid container spacing={3} sx={{ mt: 3, maxWidth: 1000 }}>
              <Stack direction="row" spacing={2} sx={{ alignItems: 'left' }}>
                <Iconify
                  icon="carbon:flight-international"
                  width={130}
                  sx={{ color: '#000', transition: 'color 0.3s ease-in-out' }}
                  onMouseEnter={(e) => (e.target.style.color = '#000')}
                  onMouseLeave={(e) => (e.target.style.color = '#000')}
                />

                <div>
                  <m.div variants={varFade().in}>
                    <Typography
                      variant="h5"
                      sx={{
                        color: 'common.black',
                      }}
                    >
                      International
                    </Typography>
                  </m.div>
                  <m.div variants={varFade().inUp}>
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: 'left',
                        lineHeight: 1.3,
                        mb: 3,
                        fontSize: '1.2rem',
                        color: 'common.black',
                      }}
                    >
                      Our commitment to international shipping excellence outpaces the competition with unique offerings that exceed client
                      expectations.
                    </Typography>
                  </m.div>
                  <Button
                    component={RouterLink}
                    to={'/international'}
                    size="small"
                    variant="outlined"
                    endIcon={<Iconify icon="ic:round-arrow-right-alt" width={24} />}
                    sx={{
                      color: 'common.black',
                      transition: 'background-color 0.3s ease-in-out, color 0.3s ease-in-out',
                      '&:hover': {
                        color: 'primary.main',
                        transform: 'translateY(-5px)',
                        transition: 'transform 0.3s ease-in-out',
                      },
                    }}
                  >
                    Learn More
                  </Button>
                </div>
              </Stack>
            </Grid>
            <Divider sx={{ m: 1 }} />
            <Grid container spacing={3} sx={{ mt: 3, maxWidth: 1000 }}>
              <Stack direction="row" spacing={2} sx={{ alignItems: 'left' }}>
                <Iconify
                  icon="guidance:rv-trailer"
                  width={145}
                  sx={{ color: '#000', transition: 'color 0.3s ease-in-out' }}
                  onMouseEnter={(e) => (e.target.style.color = '#000')}
                  onMouseLeave={(e) => (e.target.style.color = '#000')}
                />

                <div>
                  <m.div variants={varFade().in}>
                    <Typography
                      variant="h5"
                      sx={{
                        color: 'common.black',
                      }}
                    >
                      Intermodal
                    </Typography>
                  </m.div>
                  <m.div variants={varFade().inUp}>
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: 'left',
                        lineHeight: 1,
                        mb: 3,
                        fontSize: '1.2rem',
                        color: 'common.black',
                      }}
                    >
                      From roadways to railways, we empower you to move your loads faster and more efficiently with our intermodal shipping
                      services tailored to your unique needs.
                    </Typography>
                  </m.div>
                  <Button
                    component={RouterLink}
                    to={PATH_PAGE.services}
                    size="small"
                    variant="outlined"
                    endIcon={<Iconify icon="ic:round-arrow-right-alt" width={24} />}
                    sx={{
                      color: 'common.black',
                      transition: 'background-color 0.3s ease-in-out, color 0.3s ease-in-out',
                      '&:hover': {
                        color: 'primary.main',
                        transform: 'translateY(-5px)',
                        transition: 'transform 0.3s ease-in-out',
                      },
                    }}
                  >
                    Learn More
                  </Button>
                </div>
              </Stack>
            </Grid>
            <Divider sx={{ m: 1 }} />
            <Grid container spacing={3} sx={{ mt: 3, maxWidth: 1000 }}>
              <Stack direction="row" spacing={2} sx={{ alignItems: 'left' }}>
                <Iconify
                  icon="game-icons:crane"
                  width={150}
                  sx={{ color: '#000', transition: 'color 0.3s ease-in-out' }}
                  onMouseEnter={(e) => (e.target.style.color = '#000')}
                  onMouseLeave={(e) => (e.target.style.color = '#000')}
                />

                <div>
                  <m.div variants={varFade().in}>
                    <Typography
                      variant="h5"
                      sx={{
                        color: 'common.black',
                      }}
                    >
                      Drayage
                    </Typography>
                  </m.div>
                  <m.div variants={varFade().inUp}>
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: 'left',
                        lineHeight: 1,
                        mb: 3,
                        fontSize: '1.2rem',
                        color: 'common.black',
                      }}
                    >
                      We bridge the gaps between domestic and international transportation with reliable drayage services that serve every
                      port and railway in the US, driving endless shipping possibilities.
                    </Typography>
                  </m.div>
                  <Button
                    component={RouterLink}
                    to={PATH_PAGE.services}
                    size="small"
                    variant="outlined"
                    endIcon={<Iconify icon="ic:round-arrow-right-alt" width={24} />}
                    sx={{
                      color: 'common.black',
                      transition: 'background-color 0.3s ease-in-out, color 0.3s ease-in-out',
                      '&:hover': {
                        color: 'primary.main',
                        transform: 'translateY(-5px)',
                        transition: 'transform 0.3s ease-in-out',
                      },
                    }}
                  >
                    Learn More
                  </Button>
                </div>
              </Stack>
            </Grid>
            <Divider sx={{ m: 1 }} />
            <Grid container spacing={3} sx={{ mt: 3, maxWidth: 1000 }}>
              <Stack direction="row" spacing={2} sx={{ alignItems: 'left' }}>
                <Iconify
                  icon="maki:warehouse"
                  width={150}
                  sx={{ color: '#000', transition: 'color 0.3s ease-in-out' }}
                  onMouseEnter={(e) => (e.target.style.color = '#000')}
                  onMouseLeave={(e) => (e.target.style.color = '#000')}
                />

                <div>
                  <m.div variants={varFade().in}>
                    <Typography
                      variant="h5"
                      sx={{
                        color: 'common.black',
                      }}
                    >
                      Warehousing and Distribution
                    </Typography>
                  </m.div>
                  <m.div variants={varFade().inUp}>
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: 'left',
                        lineHeight: 1,
                        mb: 3,
                        fontSize: '1.2rem',
                        color: 'common.black',
                      }}
                    >
                      Our scalable warehousing and distribution services help you transport, fulfill, manage, and power your end-to-end
                      logistics, allowing your products to effortlessly flow through the entire value chain.
                    </Typography>
                  </m.div>
                  <Button
                    component={RouterLink}
                    to={PATH_PAGE.services}
                    size="small"
                    variant="outlined"
                    endIcon={<Iconify icon="ic:round-arrow-right-alt" width={24} />}
                    sx={{
                      color: 'common.black',
                      transition: 'background-color 0.3s ease-in-out, color 0.3s ease-in-out',
                      '&:hover': {
                        color: 'primary.main',
                        transform: 'translateY(-5px)',
                        transition: 'transform 0.3s ease-in-out',
                      },
                    }}
                  >
                    Learn More
                  </Button>
                </div>
              </Stack>
            </Grid>
          </Stack>
        </Box>
      </Container>
    </StyledRoot>
  )
}
